/**
 * Utility per validazioni comuni
 * Contiene tutte le funzioni di validazione riutilizzabili nell'applicazione
 */

export type ValidationResult = {
    isValid: boolean;
    message: string;
};

export type BusinessHours = {
    open?: string | null;
    close?: string | null;
    start_break?: string | null;
    end_break?: string | null;
};

/**
 * Validazione email con regex
 * @param email - L'indirizzo email da validare
 */
export const validateEmail = (email: string): ValidationResult => {
    const emailRegex =
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
        return { isValid: false, message: 'Indirizzo email non valido' };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione orari di lavoro e pause
 * Controlla la coerenza degli orari di apertura/chiusura e delle pause
 */
export const validateBusinessHours = (
    hours: BusinessHours
): ValidationResult => {
    const { open, close, start_break, end_break } = hours;

    // Validazione orari apertura/chiusura
    if (open && !close) {
        return {
            isValid: false,
            message: "Inserisci l'orario di chiusura",
        };
    }

    if (close && !open) {
        return {
            isValid: false,
            message: "Inserisci l'orario di apertura",
        };
    }

    if (close && open && close < open) {
        return {
            isValid: false,
            message:
                "L'orario di chiusura deve essere successivo all'orario di apertura",
        };
    }

    // Validazione pause
    if ((start_break || end_break) && !(open && close)) {
        return {
            isValid: false,
            message:
                "Inserisci l'orario di apertura e chiusura per impostare le pause",
        };
    }

    if (start_break && !end_break) {
        return {
            isValid: false,
            message: "Inserisci l'orario di fine pausa",
        };
    }

    if (end_break && !start_break) {
        return {
            isValid: false,
            message: "Inserisci l'orario di inizio pausa",
        };
    }

    if (end_break && start_break && end_break < start_break) {
        return {
            isValid: false,
            message:
                "L'orario di fine pausa deve essere successivo all'orario di inizio pausa",
        };
    }

    if (start_break && open && start_break < open) {
        return {
            isValid: false,
            message:
                "L'orario di inizio pausa deve essere successivo all'orario di apertura",
        };
    }

    if (end_break && close && end_break > close) {
        return {
            isValid: false,
            message:
                "L'orario di fine pausa deve essere precedente all'orario di chiusura",
        };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione numero di telefono
 */
export const validatePhone = (phone: string): ValidationResult => {
    const phonePattern = /^\+?[0-9\s\-()]+$/;
    if (!phonePattern.test(phone)) {
        return { isValid: false, message: 'Numero di telefono non valido' };
    }

    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 6 || digitsOnly.length > 15) {
        return { isValid: false, message: 'Numero di telefono non valido' };
    }

    return { isValid: true, message: '' };
};

/**
 * Riduce un numero a una forma confrontabile: solo cifre, ultime 9.
 * Serve a riconoscere che "+39 340 1234567" e "3401234567" sono lo stesso numero.
 * Ritorna null se non c'è nemmeno una cifra.
 */
export const normalizePhoneForMatch = (phone: string): string | null => {
    const digits = phone.replace(/\D/g, '');
    if (!digits) return null;
    return digits.length > 9 ? digits.slice(-9) : digits;
};

/**
 * Validazione nome e cognome
 */
export const validateName = (
    name: string,
    fieldName = 'Nome'
): ValidationResult => {
    const nameRegex = /^[a-zA-ZÀ-ÿ\s'.-]{2,30}$/;
    if (!nameRegex.test(name.trim())) {
        return {
            isValid: false,
            message: `Il ${fieldName} deve contenere solo lettere, spazi, apostrofi e trattini (2-30 caratteri)`,
        };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione nome dello shop
 */
export const validateShopName = (name: string): ValidationResult => {
    const nameRegex = /^[a-zA-ZÀ-ÿ0-9\s'.-]{2,30}$/;
    if (!nameRegex.test(name.trim())) {
        return {
            isValid: false,
            message:
                "Il nome dello shop deve contenere solo lettere, spazi, apostrofi, trattini e numeri (2-30 caratteri)",
        };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione Partita IVA italiana
 */
export const validateVatId = (vatId: string): ValidationResult => {
    const vatRegex = /^(IT)?\d{11}$/;
    if (!vatRegex.test(vatId)) {
        return { isValid: false, message: 'Partita IVA non valida' };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione indirizzo italiano generico
 */
export const validateItalianAddress = (
    address: string
): ValidationResult => {
    if (!address || !address.trim()) {
        return { isValid: false, message: 'Indirizzo è richiesto' };
    }

    const addressRegex =
        /^(?:(via|viale|corso|c\.?so|piazza|p\.?za|p\.?le|largo|vicolo|vico|strada|sda|contrada|c\.?da|località|loc\.?|fraz\.?|frazione|borgo|b\.?go|rione)\s+)?([a-zA-ZÀ-ÿ0-9\s'’.:-]+?)(?:\s*,?\s*(\d{1,4}(?:\s*[A-Za-z]|\/[A-Za-z0-9])?(?:\s*-\s*\d{1,4})?))?\s*$/i;
    if (!addressRegex.test(address.trim())) {
        return { isValid: false, message: 'Formato indirizzo non valido' };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione CAP italiano
 */
export const validateItalianZip = (zip: string): ValidationResult => {
    if (!/^\d{5}$/.test(zip)) {
        return {
            isValid: false,
            message: 'CAP non valido (deve essere di 5 cifre)',
        };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione password
 */
export const validatePassword = (
    password: string,
    minLength = 8
): ValidationResult => {
    if (password.trim() !== password) {
        return {
            isValid: false,
            message: 'La password non può iniziare o terminare con uno spazio',
        };
    }

    if (password.length < minLength) {
        return {
            isValid: false,
            message: `La password deve essere di almeno ${minLength} caratteri`,
        };
    }

    const passwordRegex =
        /^(?!\s)(?!.*\s$)(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;
    if (!passwordRegex.test(password)) {
        return {
            isValid: false,
            message:
                'Password non valida (minimo 8 caratteri, maiuscole, minuscole, numeri, simboli)',
        };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione conferma password
 */
export const validatePasswordConfirmation = (
    password: string,
    confirmPassword: string
): ValidationResult => {
    if (password !== confirmPassword) {
        return { isValid: false, message: 'Le password non coincidono' };
    }

    return { isValid: true, message: '' };
};

/**
 * Validazione URL del sito web
 */
export const validateWebsite = (website: string): ValidationResult => {
    const url = website.trim();

    const websiteRegex =
        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;
    if (!websiteRegex.test(url)) {
        return {
            isValid: false,
            message: 'URL del sito web non valido',
        };
    }

    return { isValid: true, message: '' };
};